import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Button } from "react-migration/components/Button";
import { DividerV2 } from "react-migration/components/DividerV2";
import { Icon } from "react-migration/components/Icon";
import { LtIconKey } from "react-migration/domains/constraints/designation/style/icons";
import { SwitchWorkspaceModal } from "react-migration/domains/workspaces/components/SwitchWorkspaceModal";
import { useWorkspaceSwitchGate } from "react-migration/domains/workspaces/hooks/useWorkspaceSwitchGate";
import { Theme } from "react-migration/lib/theme/Theme";
import { isDefined } from "react-migration/lib/util/isDefined";
import { logEventV2 } from "react-migration/lib/util/logEventV2";
import { useOperatingSystem } from "react-migration/lib/util/useOperatingSystem";
import { useNavigate } from "src/js/router/react-router";
import { clearGlobalSearch, updateGlobalSearch } from "src/js/stores/map/actions/updateSearch";
import { updateSelection } from "src/js/stores/map/actions/updateSelection";
import { mapStore } from "src/js/stores/map/store";
import { showError } from "src/js/stores/user/actions/addNotification";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";
import { useLocalStorage } from "usehooks-ts";
import { useSnapshot } from "valtio/react";
import { Tooltip } from "../Tooltip";
import {
  EmptyStateContent,
  KeyboardKey,
  LoadingIndicator,
  RecentSelectionsContent,
  SearchIconLoadingIndicator,
  SearchResultsContent,
  SectionButton,
} from "./components";
import { ErrorWithToast } from "./ErrorWithToast";
import { AutocompleteResult, AutocompleteSectionId, RecentSelection, SearchAutocompleteSection } from "./types";
import { getMapCoordinatesForNavigation, navigateToMap } from "./util";

/** Dispatched on `window` to open the global search modal */
export const GLOBAL_SEARCH_OPEN_EVENT = "global-search:open";

type GlobalSearchBaseProps = {
  className?: string;
  placeholder?: string;
  inputClassName?: string;
  icon?: LtIconKey;
  sections: SearchAutocompleteSection[];
  sectionsWhitelist?: AutocompleteSectionId[];

  // @deprecated used to place pin in old location search, as well as to log location search events
  onSearchLocationChange?(location: null | { geometry: any; place: any }): void;
};

function GlobalSearchBase({
  className,
  inputClassName,
  placeholder = "Search...",
  icon = "lt-search-line",
  sections: unfilteredSections,
  sectionsWhitelist,
  onSearchLocationChange,
}: GlobalSearchBaseProps) {
  const isUSUser = hasFeature(Feature.usAccess);
  const navigateReact = useNavigate();
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  const highlightedRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingSelectionRef = useRef<{ start: number; end: number } | null>(null);
  const os = useOperatingSystem();

  const { googleMap, globalSearch } = useSnapshot(mapStore);

  const { checkWorkspaceAccess, modalProps } = useWorkspaceSwitchGate({ source: "global_search" });

  const [isHandlingSelection, setIsHandlingSelection] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState<AutocompleteSectionId | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [loadingSections, setLoadingSections] = useState<Set<AutocompleteSectionId>>(new Set());
  const [isMouseActive, setIsMouseActive] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [, setSectionErrors] = useState<Map<AutocompleteSectionId, Error>>(new Map());

  const [searchResults, setSearchResults] = useState(new Map<AutocompleteSectionId, AutocompleteResult[]>());

  const [recentSelections, setRecentSelections] = useLocalStorage<RecentSelection[]>("recent-searches", []);

  const shortcutKey = useMemo(() => {
    return os === "mac" ? "⌘ K" : "Ctrl+K";
  }, [os]);

  useEffect(() => {
    const onOpenRequest = () => setIsModalOpen(true);
    globalThis.addEventListener(GLOBAL_SEARCH_OPEN_EVENT, onOpenRequest);
    return () => globalThis.removeEventListener(GLOBAL_SEARCH_OPEN_EVENT, onOpenRequest);
  }, []);

  useLayoutEffect(() => {
    const inputElement = inputRef.current;
    if (!inputElement) return;
    const pending = pendingSelectionRef.current;
    if (pending) {
      const len = inputElement.value.length;
      inputElement.selectionStart = Math.min(pending.start, len);
      inputElement.selectionEnd = Math.min(pending.end, len);
    }
  });

  const sections = useMemo(() => {
    if (!!sectionsWhitelist?.length) {
      return unfilteredSections.filter((section) => sectionsWhitelist.includes(section.id));
    }

    return unfilteredSections;
  }, [unfilteredSections, sectionsWhitelist]);

  // Hydrate recent selections with their sections, filtering out any that don't have matching sections
  const hydratedRecentSelections = useMemo(
    () =>
      recentSelections
        .map((selection: RecentSelection) => {
          const section = sections.find((s: SearchAutocompleteSection) => s.id === selection.sectionId);
          return section ? { ...selection, section } : null;
        })
        .filter(isDefined),
    [recentSelections, sections]
  );

  // Scroll highlighted item into view when it changes
  useEffect(() => {
    if (highlightedRef.current) {
      highlightedRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  // Fetch search results when search term changes
  useEffect(
    function fetchAutocompleteSectionResults() {
      let cancelled = false;

      // Abort current search if any
      const abortCurrentSearch = () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
      };

      const fetchSectionResults = async (section: SearchAutocompleteSection, searchTerm: string) => {
        if (cancelled) return;

        setLoadingSections((prev: Set<AutocompleteSectionId>) => new Set(prev).add(section.id));
        try {
          const sectionResults = await section.fetchResults(searchTerm, abortControllerRef.current!.signal);

          if (!cancelled) {
            setSearchResults((prev: Map<AutocompleteSectionId, AutocompleteResult[]>) =>
              new Map(prev).set(section.id, sectionResults)
            );
          }
        } catch (error) {
          if (!cancelled) {
            setSectionErrors((prev: Map<AutocompleteSectionId, Error>) =>
              new Map(prev).set(section.id, error as Error)
            );
          }
        } finally {
          if (!cancelled) {
            setLoadingSections((prev: Set<AutocompleteSectionId>) => {
              const next = new Set(prev);
              next.delete(section.id);
              return next;
            });
          }
        }
      };

      onSearchLocationChange?.(null);
      setSectionErrors(new Map());
      setHighlightedIndex(0);
      setSelectedSectionId(null);
      setSearchResults(new Map());

      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = setTimeout(() => {
        abortControllerRef.current = new AbortController();

        for (const section of sections) {
          fetchSectionResults(section, globalSearch);
        }
      }, 300);

      return () => {
        cancelled = true;
        abortCurrentSearch();
        clearTimeout(debounceTimeoutRef.current);
      };
    },
    [globalSearch, sections, onSearchLocationChange]
  );

  const performSelection = useCallback(
    async (
      result: AutocompleteResult,
      section: SearchAutocompleteSection,
      options?: { skipRecentUpdate?: boolean }
    ) => {
      const hadMapInstance = googleMap != null;

      try {
        clearGlobalSearch();
        inputRef.current?.blur();
        setIsModalOpen(false);
        setIsHandlingSelection(true);

        const eventProperties = await section.onSelectionHandler(result, {
          map: googleMap ?? undefined,
          setSelection: updateSelection,
          onSearchLocationChange,
        });

        logEventV2({
          name: "Location Search",
          properties: {
            type: section.id,
            id: result.id,
            recentSearch: options?.skipRecentUpdate || false,
            country: isUSUser ? "US" : "UK",
            ...eventProperties,
          },
        });

        if (!hadMapInstance) {
          const coords = getMapCoordinatesForNavigation(section, eventProperties);
          navigateToMap(coords, navigateReact);
        }

        if (!options?.skipRecentUpdate) {
          const isDuplicate = recentSelections.some(
            (selection) => selection.result.id === result.id && selection.sectionId === section.id
          );

          if (!isDuplicate) {
            setRecentSelections((prev: RecentSelection[]) => {
              const newSelection = { result, sectionId: section.id };
              return [newSelection, ...prev].slice(0, 5);
            });
          }
        }
      } catch (e) {
        showError({
          id: "global-search_selection-error",
          title: "Unable to make selection",
          description: (e as ErrorWithToast)?.toast,
          duration: 10_000,
        });

        console.error(`Failed to handle selection for ${section.title} with id ${result.id}`, e);
      } finally {
        setIsHandlingSelection(false);
      }
    },
    [googleMap, onSearchLocationChange, recentSelections, setRecentSelections, isUSUser, navigateReact]
  );

  const requestSelect = useCallback(
    (result: AutocompleteResult, section: SearchAutocompleteSection, options?: { skipRecentUpdate?: boolean }) => {
      if (section.id === "sites") {
        const workspaceId = result.workspace?.id;
        checkWorkspaceAccess(workspaceId, result.workspace?.title ?? "", () =>
          performSelection(result, section, options)
        );
        return;
      }

      performSelection(result, section, options);
    },
    [performSelection, checkWorkspaceAccess]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) {
        pendingSelectionRef.current = null;
      }

      if (!sections.length) return;

      if (["ArrowDown", "ArrowUp", "Enter"].includes(e.key)) {
        e.preventDefault();
      }

      const recentSelectionItems = hydratedRecentSelections.map(
        ({ result, section }: { result: AutocompleteResult; section: SearchAutocompleteSection }) => ({
          result,
          section,
        })
      );

      const searchResultItems = sections.flatMap((section: SearchAutocompleteSection) => {
        const results = searchResults.get(section.id) || [];
        return results.map((result: AutocompleteResult) => ({ result, section }));
      });

      const usingRecentSection = !globalSearch && hydratedRecentSelections.length > 0;
      const currentItems = usingRecentSection ? recentSelectionItems : searchResultItems;

      if (currentItems.length === 0) return;

      // Set mouse inactive when using keyboard
      setIsMouseActive(false);

      if (e.key === "ArrowDown") {
        setHighlightedIndex((prev: number) => (prev + 1) % currentItems.length);
      } else if (e.key === "ArrowUp") {
        setHighlightedIndex((prev: number) => (prev - 1 + currentItems.length) % currentItems.length);
      } else if (e.key === "Enter") {
        const item = currentItems[highlightedIndex];
        if (item) {
          requestSelect(item.result, item.section, { skipRecentUpdate: usingRecentSection });
        }
      }
    },
    [searchResults, highlightedIndex, requestSelect, sections, globalSearch, hydratedRecentSelections]
  );

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, selectionStart, selectionEnd } = e.target;
    pendingSelectionRef.current = {
      start: selectionStart ?? 0,
      end: selectionEnd ?? 0,
    };
    updateGlobalSearch(value);
  }, []);

  // Filter sections based on selection
  const visibleSections = selectedSectionId
    ? sections.filter((section: SearchAutocompleteSection) => section.id === selectedSectionId)
    : sections;

  const triggerClassName = clsx(
    "rounded-lg border px-1 pl-9 md:pr-3 grow w-full md:w-[330px]",
    "bg-background-input border-border-action",
    "h-12 md:h-9 text-md md:text-sm",
    "shadow-map-button md:shadow-xs",
    "text-left font-normal",
    "cursor-pointer",
    "hover:bg-background-input-hover",
    inputClassName
  );

  useEffect(() => {
    const handleCmdKKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        if (!isModalOpen) {
          logEventV2({
            name: "Location Search Opened",
            properties: {
              type: "Keyboard Shortcut",
            },
          });
        }
        setIsModalOpen((prev: boolean) => !prev);
      }
    };

    globalThis.addEventListener("keydown", handleCmdKKeyDown);

    return () => {
      globalThis.removeEventListener("keydown", handleCmdKKeyDown);
    };
  }, []);

  return (
    <>
      <Dialog.Root
        open={isModalOpen}
        onOpenChange={(open: boolean) => {
          setIsModalOpen(open);
          if (open) {
            setHighlightedIndex(0);
            logEventV2({
              name: "Location Search Opened",
              properties: {
                type: "Mouse Click",
              },
            });
          } else {
            setHighlightedIndex(0);
            setSelectedSectionId(null);
            setIsMouseActive(false);
            pendingSelectionRef.current = null;
          }
        }}
      >
        <div className={clsx("flex flex-1 mx-auto justify-center w-full", className)}>
          <div className="w-full px-1 md:w-auto md:px-0">
            <SearchIconLoadingIndicator
              icon={icon}
              loading={isHandlingSelection}
              className="absolute pointer-events-none"
            />
            <Tooltip.Wrapper triggerMode="hover" delayDuration={50}>
              <Tooltip.Content size="sm" side="top" align="center" theme={Theme.Light}>
                <Tooltip.Description className="flex no-wrap items-center gap-1.5">
                  Open search <KeyboardKey kbdKey={shortcutKey} />
                </Tooltip.Description>
              </Tooltip.Content>
              <Dialog.Trigger className="w-full">
                <Tooltip.Trigger>
                  <button
                    type="button"
                    data-testid="global-search__trigger"
                    aria-expanded={isModalOpen}
                    aria-haspopup="dialog"
                    disabled={isHandlingSelection}
                    className={triggerClassName}
                  >
                    <span className="block w-full truncate text-text-base-tertiary">
                      Search...{" "}
                      <span className="hidden md:inline-block text-text-base-tertiary/70 text-xs">{shortcutKey}</span>
                    </span>
                  </button>
                </Tooltip.Trigger>
              </Dialog.Trigger>
            </Tooltip.Wrapper>
          </div>
        </div>
        <Dialog.Portal>
          <Dialog.Overlay
            className={clsx(
              "fixed inset-0 z-90 bg-black/30 backdrop-blur-xs",
              "data-[state=open]:animate-overlay-show"
            )}
          />
          <Dialog.Content
            onOpenAutoFocus={(e: Event) => {
              e.preventDefault();
              queueMicrotask(() => inputRef.current?.focus());
            }}
            onCloseAutoFocus={(e: Event) => e.preventDefault()}
            className={clsx(
              "fixed z-90 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 outline-none",
              "flex flex-col",
              "bg-white drop-shadow-lg md:rounded-lg",
              "md:max-w-[680px] w-full md:max-h-[420px] h-full",
              "data-[state=open]:animate-content-show"
            )}
          >
            <Dialog.Title className="sr-only">Search</Dialog.Title>
            <Dialog.Description className="sr-only">
              Search results and recent selections appear below the field.
            </Dialog.Description>
            <div className="relative h-[65px] w-full px-6 flex items-center justify-between no-wrap gap-3 border-b border-border-base-divider">
              <Icon
                icon={`icon-${icon}`}
                size="lg"
                extraClasses="flex items-center justify-center text-text-base-tertiary"
              />
              <div className="w-full">
                <input
                  id="global-search__input"
                  data-testid="global-search__input"
                  ref={inputRef}
                  type="search"
                  autoComplete="off"
                  placeholder={placeholder}
                  value={globalSearch}
                  onChange={handleSearchChange}
                  onMouseDown={() => {
                    pendingSelectionRef.current = null;
                  }}
                  onKeyDown={handleKeyDown}
                  onBlur={() => {
                    pendingSelectionRef.current = null;
                  }}
                  disabled={isHandlingSelection}
                  className={clsx(
                    "w-full h-6 rounded-lg border px-3 py-2 shrink-0",
                    "flex items-center",
                    "bg-background-input border-none outline-none placeholder:text-text-base-tertiary",
                    "text-base placeholder:text-base truncate"
                  )}
                />
              </div>
            </div>

            <div
              className="flex flex-col flex-1 min-h-0 px-6 pt-2 "
              onMouseDown={(e) => e.preventDefault()}
              onMouseMove={() => setIsMouseActive(true)}
            >
              <div className="relative shrink-0">
                <div className="flex gap-4 pb-1">
                  <SectionButton
                    active={!selectedSectionId}
                    disabled={!selectedSectionId}
                    onClick={() => {
                      setSelectedSectionId(null);
                      setHighlightedIndex(0);
                    }}
                  >
                    All
                  </SectionButton>
                  {sections.map((section: SearchAutocompleteSection) => {
                    const hasResults = !!searchResults.get(section.id)?.length;

                    return (
                      <SectionButton
                        key={section.id}
                        disabled={!hasResults}
                        active={selectedSectionId === section.id}
                        onClick={() => {
                          if (hasResults) {
                            setSelectedSectionId(section.id);
                            setHighlightedIndex(0);
                          }
                        }}
                      >
                        {section.title}
                      </SectionButton>
                    );
                  })}
                </div>
                <div className="absolute top-0 right-0">
                  <LoadingIndicator show={loadingSections.size > 0} />
                </div>
              </div>
              <DividerV2 className="my-2 h-[2px]" />
              <div className="flex-1 overflow-y-auto min-h-0 pb-4" style={{ scrollPadding: 32 }}>
                {globalSearch ? (
                  !searchResults.size ? (
                    <EmptyStateContent type="searching" />
                  ) : Array.from(searchResults.values()).flat().length > 0 ? (
                    <SearchResultsContent
                      visibleSections={visibleSections}
                      searchResults={searchResults}
                      highlightedIndex={highlightedIndex}
                      isMouseActive={isMouseActive}
                      handleSelect={requestSelect}
                      highlightedRef={highlightedRef}
                    />
                  ) : (
                    <EmptyStateContent type="no-results" />
                  )
                ) : (
                  <RecentSelectionsContent
                    hydratedRecentSelections={hydratedRecentSelections}
                    highlightedIndex={highlightedIndex}
                    isMouseActive={isMouseActive}
                    handleSelect={requestSelect}
                    highlightedRef={highlightedRef}
                    clearRecentSelections={() => setRecentSelections([])}
                  />
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 w-full px-6 py-2 text-text-base-tertiary rounded-b-lg md:bg-blue-grey-50 md:border-t md:border-border-base-divider">
              <div className="hidden md:flex items-center justify-between gap-2 w-full text-xs">
                <span className="flex no-wrap items-center gap-1.5">
                  Press <KeyboardKey kbdKey={"ESC"} /> to close
                </span>
                <span className="flex no-wrap items-center gap-1.5">
                  Press <KeyboardKey kbdKey={<Icon icon="icon-lt-arrow-down-line" size="sm" />} />
                  <KeyboardKey kbdKey={<Icon icon="icon-lt-arrow-up-line" size="sm" />} /> to navigate
                </span>
              </div>
              <Button
                variant="tertiary"
                size="md"
                className="md:hidden self-end"
                onClick={() => setIsModalOpen(false)}
                aria-label="Close search"
              >
                Close
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <SwitchWorkspaceModal {...modalProps} />
    </>
  );
}

export type GlobalSearchProps = Omit<GlobalSearchBaseProps, "sections">;

type CreateLocationSearchArgs = {
  sections: SearchAutocompleteSection[];
  placeholder?: string;
};

export function createLocationSearch({ sections, placeholder }: Readonly<CreateLocationSearchArgs>) {
  return function LocationSearchWithSections(props: GlobalSearchProps) {
    return <GlobalSearchBase sections={sections} placeholder={placeholder} {...props} />;
  };
}
